import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import LoadingScreen from "@/components/shared/LoadingScreen";

// Decode faster than this after the src was set → the image was already at hand
// (memory/browser cache); reveal it instantly instead of the 300ms crossfade.
const INSTANT_REVEAL_MS = 150;

type ViewerImageProps = {
  /** Full-resolution image object URL (empty while it downloads/decrypts). */
  fullUrl: string;
  /** Cached thumbnail object URL — a real image, shown SHARP as the preview. */
  thumbnailUrl: string;
  /** ThumbHash data URL — the last-resort blur when no thumbnail is cached. */
  placeholderUrl?: string | null;
  /**
   * Pixels reserved at the bottom for the (persistent) controls bar, so the
   * media fits ABOVE it at scale 1. The stage itself is full-height, so a
   * zoomed image still extends behind the bar. 0 = fill the whole box.
   */
  bottomInset?: number;
  /**
   * Native pixel size from the message meta (when known). Sizes the frame on the
   * FIRST paint so a small image doesn't flash at viewport size then shrink to
   * native on open/swipe. The decoded naturalWidth (onLoad) overrides it.
   */
  naturalWidth?: number;
  naturalHeight?: number;
  /**
   * Show the fallback spinner when there is no preview at all. The video
   * poster passes false — there the download-progress overlay owns the
   * loading indicator, and two spinners were stacking on legacy videos.
   */
  spinner?: boolean;
};

/**
 * Thumbnail-first image for the attachment viewer.
 *
 * Shows the best available preview the instant the viewer opens, then
 * CROSSFADES the full-resolution image in over it — instead of an abrupt swap.
 * Preview preference: cached thumbnail (a real ~560px image, shown SHARP — only
 * soft from upscaling) → embedded ThumbHash placeholder (a ~32px blur, the only
 * layer that genuinely needs blurring) → spinner (legacy items with neither).
 *
 * The reveal is a proper blur-up crossfade:
 *   1. same box — every layer fills the identical rectangle (no layout shift);
 *   2. decode-gated — the full image is revealed only once it's DECODED
 *      (paint-ready), so the fade can't stutter on a decode-on-first-paint;
 *   3. both layers held through the fade — the full fades in OVER the preview,
 *      and the preview is dropped only once the fade finishes, so there's no
 *      unmount/mount "state refresh" flash and no backdrop showing through.
 */
const ViewerImage = ({
  fullUrl,
  thumbnailUrl,
  placeholderUrl,
  bottomInset = 0,
  naturalWidth,
  naturalHeight,
  spinner = true,
}: ViewerImageProps) => {
  // WHICH full url has decoded (→ fade it in) and WHICH has finished fading in
  // (→ drop the preview beneath it). Tracking the url, not a bare boolean, means
  // a source change (swiping to the next item, or a re-fetch) drops opacity to 0
  // in the SAME render — so the reused <img>, which keeps the PREVIOUS item's
  // decoded pixels until the new src loads, never flashes before the crossfade
  // restarts. The transition class drops in that same render too (see the img's
  // className), so the 1→0 is a snap, not a 300ms ghost fade of the old pixels.
  const [readyUrl, setReadyUrl] = useState("");
  const [fadedUrl, setFadedUrl] = useState("");
  // Urls that decoded fast enough to reveal WITHOUT the 300ms crossfade: an
  // already-cached/decoded image (memory-cache hit, swipe-back) reaching sharp
  // via a long fade reads as needless re-loading. Fast decode → appear at once.
  const [instantUrl, setInstantUrl] = useState("");
  // When the current fullUrl was committed — the reference point for "did this
  // decode fast". Stamped in an effect (not during render) so render stays pure;
  // if onLoad ever beats the stamp, elapsed computes large → falls back to the
  // normal crossfade, which is the safe degradation.
  const srcSetAtRef = useRef(0);
  useEffect(() => {
    srcSetAtRef.current = performance.now();
  }, [fullUrl]);
  // Natural size of the CURRENT full image, keyed by url so swiping to a new
  // item invalidates it (the box falls back to fill until the new one loads).
  const [nat, setNat] = useState<{ url: string; w: number; h: number } | null>(
    null,
  );
  const fullReady = fullUrl !== "" && fullUrl === readyUrl;
  const fullFaded = fullUrl !== "" && fullUrl === fadedUrl;
  const fullInstant = fullUrl !== "" && fullUrl === instantUrl;
  // Frame-size source: prefer the DECODED size (ground truth — self-corrects
  // wrong/rotated meta); fall back to the dims passed from the message meta so
  // the frame is right on the FIRST paint, before the full image has loaded.
  // This is what stops the open/swipe "flash at 100% then shrink" on small
  // images. Null (legacy: neither known yet) → the box fills the area until load.
  const loadedNat = nat && nat.url === fullUrl ? nat : null;
  const propNat =
    naturalWidth && naturalHeight && naturalWidth > 0 && naturalHeight > 0
      ? { w: naturalWidth, h: naturalHeight }
      : null;
  const frameNat = loadedNat ?? propNat;

  const previewUrl = thumbnailUrl || placeholderUrl;
  // A real cached thumbnail is shown sharp; only the tiny ThumbHash is blurred.
  const isThumbnail = !!thumbnailUrl;

  // Reveal only once the bytes are DECODED, so the crossfade never stutters.
  // Falls back to reveal-on-load where decode() is unavailable (jsdom / very old
  // browsers). The `fullReady` url check downstream ignores a stale resolve if
  // the source changed (rapid swipe) before decode settled. We also capture the
  // full image's NATURAL size here, to cap the display box (below).
  const revealWhenDecoded = (img: HTMLImageElement) => {
    const url = fullUrl;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setNat({ url, w: img.naturalWidth, h: img.naturalHeight });
    }
    // Decoded within the window → the bytes were at hand (memory cache / browser
    // cache): reveal at once (skip the fade AND drop the preview immediately).
    // Both setStates batch with setReadyUrl, so it's one clean render.
    const reveal = () => {
      if (performance.now() - srcSetAtRef.current < INSTANT_REVEAL_MS) {
        setInstantUrl(url);
        setFadedUrl(url);
      }
      setReadyUrl(url);
    };
    if (typeof img.decode === "function") {
      img.decode().then(reveal).catch(reveal);
    } else {
      reveal();
    }
  };

  // Cap the media at its NATIVE size (centered), so a small image isn't scaled
  // UP to fill the viewport (blurry at rest) — object-contain would upscale it.
  // Only when the size is unknown (legacy message with no meta dims, not yet
  // loaded) do we fill the available area. Zoom still enlarges past this.
  const frameStyle: React.CSSProperties = frameNat
    ? {
        aspectRatio: `${frameNat.w} / ${frameNat.h}`,
        width: `${frameNat.w}px`,
        maxWidth: "100%",
        maxHeight: "100%",
      }
    : { width: "100%", height: "100%" };
  // Layers fill the frame; the frame is the real box object-contain centers in.
  const layer = "absolute inset-0 h-full w-full object-contain";

  return (
    // dvh, not vh: in iOS Safari with toolbars visible 100vh is the LARGE
    // viewport (~60-100px taller than what's really on screen), which pushed
    // the centered media below screen-center and hid tall media's bottom edge
    // behind the toolbar. dvh tracks the dynamic viewport like the app shell.
    <div className="relative h-dvh w-screen">
      {/* Reserve the controls-bar height at the bottom, and center the media
          frame in the space above it. */}
      <div
        data-testid="viewer-stage"
        className="absolute inset-x-0 top-0 flex items-center justify-center"
        style={{ height: `calc(100% - ${bottomInset}px)` }}
      >
        <div data-testid="viewer-frame" className="relative" style={frameStyle}>
          {previewUrl
            ? // Preview held through the crossfade; dropped once the full has
              // faded in (fullFaded), never earlier — so full fades in over it.
              !fullFaded && (
                <img
                  src={previewUrl}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  data-testid={
                    isThumbnail ? "viewer-thumbnail" : "viewer-placeholder"
                  }
                  className={cn(
                    // ios-image-safe: suppress the iOS long-press callout /
                    // drag preview, same as the message-bubble images.
                    "ios-image-safe",
                    layer,
                    !isThumbnail && "scale-[1.03] blur-xl",
                  )}
                />
              )
            : // No preview: spinner until the full is ready (then it fades in).
              spinner &&
              !fullReady && (
                <div className="absolute inset-0 grid place-items-center">
                  <LoadingScreen />
                </div>
              )}
          {fullUrl && (
            <img
              src={fullUrl}
              alt="Image"
              decoding="async"
              draggable={false}
              data-testid="viewer-full-image"
              onLoad={(e) => revealWhenDecoded(e.currentTarget)}
              // Fade done → drop the preview beneath. Guarded on fullReady so
              // a transitionEnd landing around a source swap never drops the
              // NEW item's preview early (the swipe-away drop itself snaps —
              // no transition class — so it can't fire this).
              onTransitionEnd={() => {
                if (fullReady) {
                  setFadedUrl(fullUrl);
                }
              }}
              className={cn(
                "ios-image-safe",
                layer,
                // Transition only while REVEALING: adding the class in the
                // same commit that flips opacity 0→1 still animates the
                // fade-in, but the swipe-away drop to 0 (url swap → fullReady
                // false) must SNAP — with the class always present, the
                // reused <img> animated 1→0 over 300ms, ghosting the previous
                // item inside the new item's frame on cached-to-cached
                // swipes. Cached/fast-decoding images (fullInstant) skip the
                // fade entirely.
                fullReady &&
                  !fullInstant &&
                  "transition-opacity duration-300 ease-out",
              )}
              style={{ opacity: fullReady ? 1 : 0 }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewerImage;
